/**
 * Shared axios instance for the backend API.
 *
 * - Attaches the in-memory JWT Bearer token after login (see authToken.ts),
 *   except on /auth/login and /auth/refresh, which authenticate via cookie
 *   (+ CSRF header) instead.
 * - Sends cookies on every request (withCredentials) so the browser includes
 *   the HttpOnly refresh_token cookie automatically.
 * - On a 401 from any other call, silently attempts one /auth/refresh and
 *   retries the original request once. Concurrent 401s share a single
 *   in-flight refresh call instead of each firing their own.
 */
import axios, { AxiosHeaders, type AxiosRequestConfig } from 'axios';
import {
  getAuthorizationHeader,
  registerAuthClient,
  setAccessToken,
  clearAccessToken,
} from './authToken';
import { getAuthRefreshUrl } from './apiConfig';
import { clearClientAuthCookies, csrfHeaderBag } from './csrf';
import { parseAuthResponse, type AuthResult } from './authResponse';
import { store } from '../store';
import { loginSuccess, logout } from '../features/auth/authSlice';
import { resetSessionRestore } from './sessionRestore';

export const http = axios.create({ withCredentials: true });
registerAuthClient(http);

type RetryableConfig = AxiosRequestConfig & { __isRetry?: boolean };

/** Cookie + CSRF auth — do not attach a Bearer access token. */
function isAuthExemptUrl(url?: string): boolean {
  if (!url) return false;
  return (
    url.endsWith('/auth/login') || url.endsWith('/auth/refresh') || url.endsWith('/auth/logout')
  );
}

http.interceptors.request.use((config) => {
  const authorization = isAuthExemptUrl(config.url) ? null : getAuthorizationHeader();
  const headers = config.headers as
    | {
        set?: (key: string, value: string) => void;
        delete?: (key: string) => void;
        Authorization?: string;
      }
    | undefined;

  if (authorization) {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    const next = config.headers as typeof headers & object;
    if (typeof next.set === 'function') {
      next.set('Authorization', authorization);
    } else {
      (next as { Authorization?: string }).Authorization = authorization;
    }
    return config;
  }

  // Explicitly strip any stale Authorization (post-logout, or on the two
  // cookie-authenticated auth endpoints, where a stale Bearer would be noise).
  if (headers) {
    if (typeof headers.delete === 'function') {
      headers.delete('Authorization');
    } else {
      delete headers.Authorization;
    }
  }

  return config;
});

/**
 * Calls POST /auth/refresh (cookie + CSRF header) and returns the new auth
 * result, or null if the refresh cookie is missing/expired/reused. Does not
 * update any state itself — callers decide what to do with the result.
 */
/** POST /api/v1/auth/refresh */
export async function refreshAccessToken(): Promise<AuthResult | null> {
  try {
    const res = await http.post(getAuthRefreshUrl(), null, {
      headers: csrfHeaderBag(),
    });
    return parseAuthResponse(res.data);
  } catch {
    return null;
  }
}

/** Concurrent 401s share this single in-flight refresh instead of each firing their own. */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * A refresh that has not answered by then stops blocking the calls queued
 * behind it. Every API call waits on the shared refresh, so a hung refresh
 * used to hang the whole UI, including Stop live. The timeout is not a
 * failed session: the waiting calls are rejected with their original 401 and
 * the user is not logged out.
 */
const REFRESH_TIMEOUT_MS = 8_000;

export class SilentRefreshTimeoutError extends Error {
  constructor() {
    super(`Session refresh did not answer within ${REFRESH_TIMEOUT_MS / 1000} seconds.`);
    this.name = 'SilentRefreshTimeoutError';
  }
}

/** Drop any in-flight silent refresh (call on explicit logout). */
export function invalidateSilentRefresh(): void {
  refreshInFlight = null;
}

function attemptSilentRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = refreshAccessToken().then((result) => {
      if (!result) return false;
      setAccessToken(result.accessToken);
      store.dispatch(
        loginSuccess({
          username: result.username,
          accessToken: result.accessToken,
          tokenType: result.tokenType,
          userId: result.userId,
        })
      );
      return true;
    });
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new SilentRefreshTimeoutError()), REFRESH_TIMEOUT_MS);
    });
    refreshInFlight = Promise.race([refresh, timeout]).finally(() => {
      if (timer) clearTimeout(timer);
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Session is truly dead (refresh failed) — drop local auth state so the app shows the login screen. */
function forceLogout(): void {
  clearAccessToken();
  clearClientAuthCookies();
  resetSessionRestore();
  invalidateSilentRefresh();
  store.dispatch(logout());
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) {
      return Promise.reject(error);
    }

    const config = error.config as RetryableConfig;

    if (isAuthExemptUrl(config.url)) {
      // /auth/refresh itself 401'd (expired or reused refresh token) — terminal.
      if (config.url?.endsWith('/auth/refresh')) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    if (config.__isRetry) {
      // Already retried once after a refresh — don't loop.
      return Promise.reject(error);
    }

    let refreshed: boolean;
    try {
      refreshed = await attemptSilentRefresh();
    } catch (refreshError) {
      if (refreshError instanceof SilentRefreshTimeoutError) {
        // Not a dead session, just an unanswered refresh: fail this call only.
        return Promise.reject(error);
      }
      throw refreshError;
    }
    if (!refreshed) {
      forceLogout();
      return Promise.reject(error);
    }

    config.__isRetry = true;
    return http(config);
  }
);
